import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale, PRICING } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, 
  Loader2, 
  Crown, 
  Store,
  Eye,
  MessageSquare,
  TrendingUp,
  Gift,
  CreditCard,
  Calendar,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}`,
  'Content-Type': 'application/json'
});

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const { isUSA, currency, region } = useLocale();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const pricingPro = PRICING[region]?.pro || { price: 599, period: 'mes', aiCredits: 100 };
  const pricingEnterprise = PRICING[region]?.enterprise || { price: 1499, period: 'mes', aiCredits: 500 };
  const isPro = user?.subscription_plan === 'pro';
  const isEnterprise = user?.subscription_plan === 'enterprise';

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error(isUSA 
        ? 'Could not verify payment. Please check your email.' 
        : 'No se pudo verificar el pago. Por favor revisa tu correo.');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/subscriptions/status/${sessionId}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();

      if (data.status === 'complete' || data.status === 'paid') {
        toast.success(isUSA ? '🎉 Subscription activated!' : '🎉 ¡Suscripción activada!');
        updateUser({ subscription_plan: 'pro' });
        window.history.replaceState({}, '', '/dashboard/subscription');
        return;
      }

      if (data.status === 'pending') {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error polling status:', error);
    }
  };

  const handleSubscribe = async (planId = 'pro') => {
    setProcessingPayment(true);
    setSelectedPlan(planId);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          plan_id: planId,
          billing_cycle: 'monthly'
        })
      });

      const data = await res.json();
      // Backend returns "url" not "checkout_url"
      if (data.url) {
        window.location.href = data.url;
      } else if (data.message) {
        toast.success(data.message);
        updateUser({ subscription_plan: 'pro' });
      } else {
        toast.error(data.detail || (isUSA ? 'Error processing payment' : 'Error al procesar el pago'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error processing payment' : 'Error al procesar el pago');
    }
    setProcessingPayment(false);
    setSelectedPlan(null);
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(isUSA 
      ? 'Are you sure you want to cancel your subscription?' 
      : '¿Estás seguro que deseas cancelar tu suscripción?')) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/subscriptions/cancel`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success(isUSA 
          ? 'Subscription cancelled. You keep access until the end of your billing period.' 
          : 'Suscripción cancelada. Mantienes acceso hasta el final de tu período de facturación.');
        updateUser({ subscription_plan: 'free' });
      }
    } catch (error) {
      toast.error(isUSA ? 'Error cancelling subscription' : 'Error al cancelar la suscripción');
    }
  };

  const benefits = isUSA ? [
    { icon: Store, text: 'Your online store' },
    { icon: Eye, text: 'Catalog visible 24/7' },
    { icon: MessageSquare, text: 'Receive quote requests' },
    { icon: TrendingUp, text: 'Visibility on the platform' },
    { icon: Sparkles, text: `${pricingPro.aiCredits} AI Credits/month` }
  ] : [
    { icon: Store, text: 'Tu tienda en línea' },
    { icon: Eye, text: 'Catálogo visible 24/7' },
    { icon: MessageSquare, text: 'Recepción de cotizaciones' },
    { icon: TrendingUp, text: 'Visibilidad dentro de la plataforma' },
    { icon: Sparkles, text: `${pricingPro.aiCredits} Créditos IA/mes` }
  ];

  const enterpriseBenefits = isUSA ? [
    { icon: CheckCircle2, text: 'Everything in PRO' },
    { icon: Crown, text: 'Multi-user access (20 users)' },
    { icon: MessageSquare, text: 'Priority support' },
    { icon: Sparkles, text: `${pricingEnterprise.aiCredits} AI Credits/month` }
  ] : [
    { icon: CheckCircle2, text: 'Todo lo de PRO' },
    { icon: Crown, text: 'Acceso multi-usuario (20 usuarios)' },
    { icon: MessageSquare, text: 'Soporte prioritario' },
    { icon: Sparkles, text: `${pricingEnterprise.aiCredits} Créditos IA/mes` }
  ];

  return (
    <div data-testid="subscription-page" className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope, sans-serif'}}>
          {isUSA ? 'Plans & Subscription' : 'Planes y Suscripción'}
        </h1>
        <p className="text-slate-600">
          {isUSA ? 'Your current plan:' : 'Tu plan actual:'}{' '}
          <Badge variant="outline" className={isPro ? "bg-emerald-100 border-emerald-400 text-emerald-700" : "bg-slate-100"}>
            {isPro ? 'TexHub Pro' : (isUSA ? 'Free' : 'Gratis')}
          </Badge>
        </p>
      </div>

      {/* Current Status for Pro Users */}
      {isPro && (
        <Card className="mb-8 border-emerald-200 bg-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-emerald-800 text-lg">
                  {isUSA ? 'You have TexHub Pro!' : '¡Tienes TexHub Pro!'}
                </h3>
                <p className="text-emerald-700 text-sm mt-1">
                  {isUSA 
                    ? 'Enjoy all benefits: online store, unlimited catalog, and quote requests.'
                    : 'Disfruta todos los beneficios: tienda en línea, catálogo ilimitado y cotizaciones.'}
                </p>
                {user?.subscription_end_date && (
                  <p className="text-emerald-600 text-xs mt-2 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {isUSA ? 'Next billing:' : 'Próximo cobro:'} {new Date(user.subscription_end_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-emerald-200">
              <Button 
                variant="ghost" 
                className="text-red-600 hover:text-red-700 hover:bg-red-50 text-sm"
                onClick={handleCancelSubscription}
              >
                {isUSA ? 'Cancel subscription' : 'Cancelar suscripción'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TexHub Pro Card */}
      <Card className={`border-2 ${isPro ? 'border-emerald-400 bg-emerald-50' : 'border-emerald-500'} shadow-xl relative overflow-hidden mb-6`}>
        {!isPro && !isEnterprise && (
          <div className="absolute top-4 right-4">
            <Badge className="bg-emerald-500 text-white">{isUSA ? 'Most Popular' : 'Más Popular'}</Badge>
          </div>
        )}
        <CardContent className="p-6">
          {/* Plan Name */}
          <div className="text-center mb-6">
            <Badge className="bg-emerald-100 text-emerald-700 px-4 py-1 text-sm font-semibold mb-3">
              PRO
            </Badge>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-4xl font-bold text-slate-900">${pricingPro.price}</span>
              <span className="text-slate-500">{currency}/{pricingPro.period}</span>
            </div>
            <p className="text-amber-600 text-sm mt-2 flex items-center justify-center gap-1">
              <Sparkles className="w-4 h-4" />
              {pricingPro.aiCredits} {isUSA ? 'AI Credits/month' : 'Créditos IA/mes'}
            </p>
          </div>

          {/* Benefits */}
          <ul className="space-y-4 mb-6">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3 text-slate-700">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="font-medium">{benefit.text}</span>
                {isPro && <CheckCircle2 className="w-5 h-5 text-emerald-500 ml-auto" />}
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          {!isPro && !isEnterprise && (
            <Button 
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg py-6"
              onClick={() => handleSubscribe('pro')}
              disabled={processingPayment}
              data-testid="subscribe-pro-btn"
            >
              {processingPayment && selectedPlan === 'pro' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {isUSA ? 'Processing...' : 'Procesando...'}
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  {isUSA ? 'Subscribe to PRO' : 'Activar PRO'}
                </>
              )}
            </Button>
          )}

          {isPro && (
            <div className="text-center">
              <Badge className="bg-emerald-500 text-white px-4 py-2">
                <CheckCircle2 className="w-4 h-4 mr-2 inline" />
                {isUSA ? 'Current Plan' : 'Plan Actual'}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enterprise Card */}
      <Card className={`border-2 ${isEnterprise ? 'border-purple-400 bg-purple-50' : 'border-slate-200'} shadow-lg relative overflow-hidden`}>
        <CardContent className="p-6">
          {/* Plan Name */}
          <div className="text-center mb-6">
            <Badge className="bg-purple-100 text-purple-700 px-4 py-1 text-sm font-semibold mb-3">
              {isUSA ? 'Enterprise' : 'Empresarial'}
            </Badge>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-4xl font-bold text-slate-900">${pricingEnterprise.price}</span>
              <span className="text-slate-500">{currency}/{pricingEnterprise.period}</span>
            </div>
            <p className="text-purple-600 text-sm mt-2 flex items-center justify-center gap-1">
              <Sparkles className="w-4 h-4" />
              {pricingEnterprise.aiCredits} {isUSA ? 'AI Credits/month' : 'Créditos IA/mes'}
            </p>
          </div>

          {/* Benefits */}
          <ul className="space-y-4 mb-6">
            {enterpriseBenefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3 text-slate-700">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-4 h-4 text-purple-600" />
                </div>
                <span className="font-medium">{benefit.text}</span>
                {isEnterprise && <CheckCircle2 className="w-5 h-5 text-purple-500 ml-auto" />}
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          {!isEnterprise && (
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold text-lg py-6"
              onClick={() => handleSubscribe('enterprise')}
              disabled={processingPayment}
              data-testid="subscribe-enterprise-btn"
            >
              {processingPayment && selectedPlan === 'enterprise' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {isUSA ? 'Processing...' : 'Procesando...'}
                </>
              ) : (
                <>
                  <Crown className="w-5 h-5 mr-2" />
                  {isUSA ? 'Subscribe to Enterprise' : 'Activar Empresarial'}
                </>
              )}
            </Button>
          )}

          {isEnterprise && (
            <div className="text-center">
              <Badge className="bg-purple-500 text-white px-4 py-2">
                <CheckCircle2 className="w-4 h-4 mr-2 inline" />
                {isUSA ? 'Current Plan' : 'Plan Actual'}
              </Badge>
            </div>
          )}

          {/* Trust Message */}
          {!isEnterprise && (
            <p className="text-xs text-slate-500 text-center mt-4">
              {isUSA 
                ? 'Secure payment via Stripe. Cancel anytime from your dashboard.' 
                : 'Pago seguro vía Stripe. Cancela cuando quieras desde tu dashboard.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* FAQ */}
      <div className="mt-8 space-y-4">
        <h3 className="font-semibold text-slate-900">
          {isUSA ? 'Frequently asked questions' : 'Preguntas frecuentes'}
        </h3>
        
        <div className="p-4 bg-slate-50 rounded-lg">
          <p className="font-medium text-slate-900 text-sm">
            {isUSA ? 'When will I be charged?' : '¿Cuándo me cobran?'}
          </p>
          <p className="text-slate-600 text-sm mt-1">
            {isUSA 
              ? 'Your card will be charged immediately when you subscribe. Subscriptions auto-renew monthly.'
              : 'Tu tarjeta se cobra al momento de suscribirte. Las suscripciones se renuevan automáticamente cada mes.'}
          </p>
        </div>
        
        <div className="p-4 bg-slate-50 rounded-lg">
          <p className="font-medium text-slate-900 text-sm">
            {isUSA ? 'Can I cancel anytime?' : '¿Puedo cancelar en cualquier momento?'}
          </p>
          <p className="text-slate-600 text-sm mt-1">
            {isUSA 
              ? 'Yes, cancel anytime from this page. You keep access until the end of your billing period.'
              : 'Sí, cancela cuando quieras desde esta página. Mantienes acceso hasta el final de tu período.'}
          </p>
        </div>
      </div>

      {/* Sponsor CTA */}
      <Card className="mt-8 p-6 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <h3 className="font-bold text-lg flex items-center gap-2 justify-center md:justify-start">
              <Crown className="w-5 h-5" />
              {isUSA ? 'Want more visibility?' : '¿Quieres más visibilidad?'}
            </h3>
            <p className="text-amber-100 text-sm mt-1">
              {isUSA 
                ? 'Become a sponsor and get featured position'
                : 'Hazte patrocinador y obtén posición destacada'}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center bg-white/20 rounded-lg px-4 py-2">
              <p className="text-xl font-bold">$1,999</p>
              <p className="text-xs text-amber-100">MXN/{isUSA ? 'month' : 'mes'}</p>
            </div>
            <Button 
              className="bg-white text-amber-600 hover:bg-amber-50 font-semibold"
              onClick={() => {
                const message = encodeURIComponent('Hola, me interesa ser patrocinador en TexHub. ¿Me pueden dar más información?');
                window.location.href = `https://wa.me/525530063425?text=${message}`;
              }}
            >
              {isUSA ? 'Contact us' : 'Ser patrocinador'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionPage;
